import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../ui/Button/Button';
import { FormInput } from '../../../ui/FormInput/FormInput';
import { ValidationMessage } from '../../../components/Forms/ValidationMessage';
import { bccEmailService } from '../../../services/bccEmailService';
import { validateEmail } from '../../../utils/validators';
import { TranslationKey } from '../../../i18n/translationKeys';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export const BccEmailPanel: React.FC = () => {
    const { t } = useTranslation();
    const [loaded, setLoaded] = useState(false);
    const [value, setValue] = useState('');
    const [saveState, setSaveState] = useState<SaveState>('idle');
    const [invalid, setInvalid] = useState(false);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const setting = await bccEmailService.get();
                if (cancelled) return;
                setValue(setting.bccEmail ?? '');
                setLoaded(true);
            } catch (e) {
                console.error('Failed to load the BCC preference:', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!loaded) return null;

    const handleSave = async () => {
        const trimmed = value.trim();
        if (trimmed && !validateEmail(trimmed)) {
            setInvalid(true);
            return;
        }
        setInvalid(false);
        setSaveState('saving');
        try {
            await bccEmailService.set(trimmed || null);
            setSaveState('saved');
        } catch (e) {
            setSaveState('error');
            console.error('Failed to save the BCC preference:', e);
        }
    };

    return (
        <div className="space-y-s">
            <h2 className="text-block-title text-content">{t(TranslationKey.Settings.BccEmail)}</h2>
            <p className="text-primary-700">{t(TranslationKey.Settings.BccEmailDescription)}</p>
            <div className="flex items-start gap-s pt-xs">
                <div className="min-w-0 flex-1 max-w-80">
                    <FormInput
                        id="settings-bcc-email"
                        data-testid="settings-bcc-email"
                        className="w-full"
                        type="email"
                        value={value}
                        onChange={(e) => { setValue(e.target.value); setSaveState('idle'); }}
                        aria-invalid={invalid}
                        aria-describedby={invalid ? 'settings-bcc-email-err' : undefined}
                    />
                    <ValidationMessage
                        id="settings-bcc-email-err"
                        data-testid="settings-bcc-email-invalid"
                        message={invalid ? t(TranslationKey.CustomerFollowUp.Email.Errors.EmailAddressInvalid) : undefined}
                    />
                </div>
                <Button data-testid="settings-bcc-email-save" onClick={() => void handleSave()} loading={saveState === 'saving'}>
                    {t(TranslationKey.Settings.BccEmailSave)}
                </Button>
            </div>
            {saveState === 'saved' && (
                <p data-testid="settings-bcc-email-saved" className="text-primary-700 text-s">{t(TranslationKey.Settings.BccEmailSaved)}</p>
            )}
            {saveState === 'error' && (
                <p data-testid="settings-bcc-email-error" className="text-danger text-s">{t(TranslationKey.Settings.BccEmailError)}</p>
            )}
        </div>
    );
};
